import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Alert, Chip, Stack,
  Card, CardContent, CardActions, Grid, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, Divider,
  useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon      from '@mui/icons-material/Add';
import CloseIcon    from '@mui/icons-material/Close';
import ListAltIcon  from '@mui/icons-material/ListAlt';
import DownloadIcon from '@mui/icons-material/Download';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PasswordDialog from './PasswordDialog';
import { getReceiptImageUrl, getBankStatementImageUrl } from '../api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function EventManager({ activeGroup, groupPassword }) {
  const [events, setEvents]             = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newEvent, setNewEvent]         = useState({ title: '', voorschot_ontvangen: '' });
  const [password, setPassword]         = useState('');
  const [message, setMessage]           = useState(null);
  const [loading, setLoading]           = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventReceipts, setEventReceipts] = useState([]);
  const [viewReceipt, setViewReceipt]     = useState(null);

  // Password dialog for closing an event
  const [closePwdDialog, setClosePwdDialog] = useState({ open: false, eventId: null });
  // Password dialog for exporting an event
  const [exportPwdDialog, setExportPwdDialog] = useState({ open: false, eventId: null });
  // Password dialog for reopening a closed event
  const [reopenPwdDialog, setReopenPwdDialog] = useState({ open: false, eventId: null });

  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadEvents(); }, [activeGroup]);

  const loadEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events`, { params: { group_name: activeGroup } });
      setEvents(response.data);
    } catch {
      setMessage({ type: 'error', text: 'Kon evenementen niet laden' });
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!newEvent.title || !newEvent.voorschot_ontvangen || !password) {
      setMessage({ type: 'error', text: 'Vul alle velden in' }); return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('title', newEvent.title);
      fd.append('voorschot_ontvangen', parseFloat(newEvent.voorschot_ontvangen));
      fd.append('password', password);
      fd.append('group_name', activeGroup);
      await axios.post(`${API_URL}/api/events`, fd);
      setMessage({ type: 'success', text: 'Evenement aangemaakt!' });
      setShowCreateForm(false);
      setNewEvent({ title: '', voorschot_ontvangen: '' });
      setPassword('');
      await loadEvents();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Kon evenement niet aanmaken' });
    } finally { setLoading(false); }
  };

  const handleClosePasswordConfirm = async (pwd) => {
    const { eventId } = closePwdDialog;
    setClosePwdDialog({ open: false, eventId: null });
    if (pwd !== groupPassword) { setMessage({ type: 'error', text: 'Onjuist wachtwoord!' }); return; }
    try {
      const fd = new FormData(); fd.append('password', pwd);
      await axios.patch(`${API_URL}/api/events/${eventId}/close`, fd);
      setMessage({ type: 'success', text: 'Evenement afgesloten' });
      await loadEvents();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Kon evenement niet afsluiten' });
    }
  };

  const handleExportEvent = async (eventId, password) => {
    try {
      const fd = new FormData(); fd.append('password', password);
      const response = await axios.post(`${API_URL}/api/export/event/${eventId}`, fd, { responseType: 'blob' });
      const url  = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd  = response.headers['content-disposition'];
      const match = cd?.match(/filename="([^"]+)"/);
      link.setAttribute('download', match ? match[1] : 'event_export.zip');
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: 'Geëxporteerd en afgesloten!' });
      await loadEvents();
    } catch (err) {
      // With responseType 'blob' the error body is a Blob, not parsed JSON —
      // read it back so we can show the real reason (e.g. "geen bonnetjes").
      let detail;
      try {
        if (err.response?.data instanceof Blob) {
          detail = JSON.parse(await err.response.data.text())?.detail;
        } else {
          detail = err.response?.data?.detail;
        }
      } catch { /* keep detail undefined */ }
      setMessage({ type: 'error', text: detail || 'Export mislukt' });
    }
  };

  const handleExportPasswordConfirm = (pwd) => {
    const { eventId } = exportPwdDialog;
    setExportPwdDialog({ open: false, eventId: null });
    if (pwd !== groupPassword) { setMessage({ type: 'error', text: 'Onjuist wachtwoord!' }); return; }
    handleExportEvent(eventId, pwd);
  };

  const handleReopenPasswordConfirm = async (pwd) => {
    const { eventId } = reopenPwdDialog;
    setReopenPwdDialog({ open: false, eventId: null });
    if (pwd !== groupPassword) { setMessage({ type: 'error', text: 'Onjuist wachtwoord!' }); return; }
    try {
      const fd = new FormData(); fd.append('password', pwd);
      await axios.patch(`${API_URL}/api/events/${eventId}/reopen`, fd);
      setMessage({ type: 'success', text: 'Evenement heropend. Alle bonnetjes staan weer open.' });
      await loadEvents();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Kon evenement niet heropenen' });
    }
  };

  const handleViewDetails = async (event) => {
    setSelectedEvent(event);
    try {
      const response = await axios.get(`${API_URL}/api/receipts`, { params: { group_name: activeGroup } });
      setEventReceipts(response.data.filter(r => r.event_id === event.id));
    } catch { setEventReceipts([]); }
  };

  const formatDate     = (s) => new Date(s).toLocaleDateString('nl-NL');
  const formatCurrency = (n) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n);

  const statusChip = (status) => {
    if (status === 'active')  return <Chip label="🟢 Actief"    color="success" size="small" />;
    if (status === 'closed')  return <Chip label="🔴 Afgesloten" color="default" size="small" />;
    return <Chip label={status} size="small" />;
  };

  const receiptStatusChip = (status) => {
    const map = { pending: 'warning', confirmed: 'success', exported: 'info' };
    return <Chip label={status} color={map[status] || 'default'} size="small" />;
  };

  return (
    <Paper elevation={1} sx={{ borderRadius: 2, p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h5" fontWeight={700}>🎯 Evenementen — {activeGroup}</Typography>
        <Button
          variant={showCreateForm ? 'outlined' : 'contained'}
          startIcon={showCreateForm ? <CloseIcon /> : <AddIcon />}
          onClick={() => setShowCreateForm(v => !v)}
          color={showCreateForm ? 'inherit' : 'primary'}
        >
          {showCreateForm ? 'Annuleren' : 'Nieuw Evenement'}
        </Button>
      </Stack>

      {message && (
        <Alert severity={message.type === 'error' ? 'error' : 'success'} sx={{ mb: 2 }}
          onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Create event form */}
      <Collapse in={showCreateForm}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 1.5 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>Nieuw Evenement Aanmaken</Typography>
          <Box component="form" onSubmit={handleCreateEvent}>
            <Stack spacing={2}>
              <TextField fullWidth size="small" label="Titel *" placeholder="bijv. Zoka Hike 2026, Zomerkamp"
                value={newEvent.title} onChange={e => setNewEvent(v => ({ ...v, title: e.target.value }))} />
              <TextField fullWidth size="small" label="Voorschot Ontvangen (€) *" type="number"
                inputProps={{ step: '0.01' }} placeholder="0.00"
                value={newEvent.voorschot_ontvangen}
                onChange={e => setNewEvent(v => ({ ...v, voorschot_ontvangen: e.target.value }))} />
              <TextField fullWidth size="small" label="Wachtwoord *" type="password"
                placeholder="Voer wachtwoord in"
                value={password} onChange={e => setPassword(e.target.value)} />
              <Box>
                <Button type="submit" variant="contained" disabled={loading}>
                  {loading ? 'Aanmaken...' : 'Evenement Aanmaken'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Collapse>

      {/* Events grid */}
      <Typography variant="h6" fontWeight={600} mb={1.5}>Evenementen</Typography>
      {events.length === 0 ? (
        <Typography variant="body2" color="text.disabled" textAlign="center" py={4}>
          Nog geen evenementen aangemaakt
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {events.map(event => (
            <Grid item xs={12} sm={6} md={4} key={event.id}>
              <Card variant="outlined" sx={{ opacity: event.status === 'closed' ? 0.75 : 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                    <Typography variant="h6" fontWeight={600} sx={{ flex: 1, mr: 1 }}>{event.title}</Typography>
                    {statusChip(event.status)}
                  </Stack>

                  <Stack spacing={0.75}>
                    {[
                      ['Voorschot',  `€ ${event.voorschot_ontvangen.toFixed(2)}`],
                      ['Uitgegeven', `€ ${event.total_spent.toFixed(2)}`],
                    ].map(([k, v]) => (
                      <Stack key={k} direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">{k}</Typography>
                        <Typography variant="body2">{v}</Typography>
                      </Stack>
                    ))}
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" fontWeight={700}>Saldo</Typography>
                      <Typography variant="body2" fontWeight={700} color={event.balance >= 0 ? 'success.main' : 'error.main'}>
                        € {event.balance.toFixed(2)}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>

                <CardActions sx={{ flexWrap: 'wrap', gap: 0.5, p: 1.5, pt: 0 }}>
                  <Button size="small" variant="outlined" startIcon={<ListAltIcon />}
                    onClick={() => handleViewDetails(event)}>
                    Details
                  </Button>
                  {event.status === 'active' ? (
                    <Button size="small" variant="contained" color="info" startIcon={<DownloadIcon />}
                      onClick={() => setExportPwdDialog({ open: true, eventId: event.id })}>
                      Exporteer & Afsluiten
                    </Button>
                  ) : (
                    <Button size="small" variant="outlined" color="warning" startIcon={<LockOpenIcon />}
                      onClick={() => setReopenPwdDialog({ open: true, eventId: event.id })}>
                      Heropenen
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Event details dialog */}
      <Dialog
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        maxWidth="md"
        fullScreen={isMobile}
      >
        {selectedEvent && (
          <>
            <DialogTitle>{selectedEvent.title} - Bonnetjes</DialogTitle>
            <DialogContent>
              {eventReceipts.length === 0 ? (
                <Typography color="text.secondary" py={2}>Nog geen bonnetjes voor dit evenement</Typography>
              ) : (
                <>
                  <Stack direction="row" spacing={4} mb={2} p={1.5} bgcolor="grey.50" borderRadius={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" textTransform="uppercase">Totaal bonnetjes</Typography>
                      <Typography variant="h6" fontWeight={700}>{eventReceipts.length}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" textTransform="uppercase">Totaal bedrag</Typography>
                      <Typography variant="h6" fontWeight={700}>{formatCurrency(selectedEvent.total_spent)}</Typography>
                    </Box>
                  </Stack>

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          {['Bon #', 'Staflid', 'Bedrag', 'Categorie', 'Datum', 'Status'].map(h => (
                            <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {eventReceipts.map(r => (
                          <TableRow key={r.id} hover sx={{ cursor: 'pointer' }}
                            onClick={() => setViewReceipt(r)}
                            title="Klik om het bonnetje te bekijken">
                            <TableCell>#{r.receipt_number}</TableCell>
                            <TableCell>{r.member_name}</TableCell>
                            <TableCell>{formatCurrency(r.amount)}</TableCell>
                            <TableCell>{r.category}</TableCell>
                            <TableCell>{formatDate(r.date)}</TableCell>
                            <TableCell>{receiptStatusChip(r.status)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedEvent(null)} color="inherit">Sluiten</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Receipt viewer (opened from the event detail table) */}
      <Dialog open={!!viewReceipt} onClose={() => setViewReceipt(null)} maxWidth="sm" fullWidth>
        {viewReceipt && (
          <>
            <DialogTitle>
              Bon #{viewReceipt.receipt_number} — {viewReceipt.member_name}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={0.5} mb={2}>
                <Typography variant="body2"><strong>Bedrag:</strong> {formatCurrency(viewReceipt.amount)}</Typography>
                <Typography variant="body2"><strong>Categorie:</strong> {viewReceipt.category}</Typography>
                <Typography variant="body2"><strong>Datum:</strong> {formatDate(viewReceipt.date)}</Typography>
                {viewReceipt.store && <Typography variant="body2"><strong>Winkel:</strong> {viewReceipt.store}</Typography>}
                {viewReceipt.remarks && <Typography variant="body2"><strong>Opmerkingen:</strong> {viewReceipt.remarks}</Typography>}
              </Stack>
              <Box component="img" src={getReceiptImageUrl(viewReceipt.id)} alt="Bonnetje"
                sx={{ maxWidth: '100%', borderRadius: 1, display: 'block' }} />
              {viewReceipt.has_bank_statement && (
                <>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mt={2} mb={0.5}>
                    Bankafschrift
                  </Typography>
                  <Box component="img" src={getBankStatementImageUrl(viewReceipt.id)} alt="Bankafschrift"
                    sx={{ maxWidth: '100%', borderRadius: 1, display: 'block' }} />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewReceipt(null)} color="inherit">Sluiten</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Password dialog for closing event */}
      <PasswordDialog
        open={closePwdDialog.open}
        onClose={() => setClosePwdDialog({ open: false, eventId: null })}
        onConfirm={handleClosePasswordConfirm}
        title="Evenement afsluiten"
        description={`Voer het wachtwoord van ${activeGroup} in om het evenement af te sluiten.`}
      />

      {/* Password dialog for exporting event */}
      <PasswordDialog
        open={exportPwdDialog.open}
        onClose={() => setExportPwdDialog({ open: false, eventId: null })}
        onConfirm={handleExportPasswordConfirm}
        title="Evenement exporteren & afsluiten"
        description={`Voer het wachtwoord van ${activeGroup} in om het evenement te exporteren en af te sluiten.`}
      />

      {/* Password dialog for reopening event */}
      <PasswordDialog
        open={reopenPwdDialog.open}
        onClose={() => setReopenPwdDialog({ open: false, eventId: null })}
        onConfirm={handleReopenPasswordConfirm}
        title="Evenement heropenen"
        description={`Voer het wachtwoord van ${activeGroup} in om het evenement te heropenen. Alle bonnetjes worden weer op 'open' gezet.`}
      />
    </Paper>
  );
}

export default EventManager;
